import { useCallback, useEffect, useState } from "react";
import { companyKpiUseCase } from "../../../di/container";
import type {
  CompanyKpiMachine,
  MesDashboardCard,
} from "../../../domain/types/company/company-kpi";
import { KPI_TYPE_OPTIONS } from "../../../domain/types/company/company-kpi";
import { Button } from "../../components/UI/Button";
import { CompanyAdminLayout } from "../../components/CompanyAdmin/CompanyAdminLayout";
import { KpiCard } from "../../components/CompanyAdmin/KpiCards/KpiCard";
import { CardEditorModal } from "../../components/CompanyAdmin/KpiCards/CardEditorModal";

/**
 * KPI 2.6 카드 대시보드 (회사 공용, 믹스패널 스타일).
 * 보드 = 카드 그리드. 각 카드는 /compute 로 차트 렌더. 카드는 정의만 저장(/cards).
 */
export const KpiCardDashboard = () => {
  const [cards, setCards] = useState<MesDashboardCard[]>([]);
  const [machines, setMachines] = useState<CompanyKpiMachine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // null = 닫힘, { card } = 열림(card=null 이면 신규)
  const [editor, setEditor] = useState<{ card: MesDashboardCard | null } | null>(null);

  const loadCards = useCallback(async () => {
    setError(null);
    try {
      setCards(await companyKpiUseCase.getCards());
    } catch (e) {
      setError(e instanceof Error ? e.message : "카드 목록을 불러오지 못했습니다.");
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [cardList, machineList] = await Promise.all([
          companyKpiUseCase.getCards(),
          companyKpiUseCase.getMachines(),
        ]);
        setCards(cardList);
        setMachines(machineList);
      } catch (e) {
        setError(e instanceof Error ? e.message : "데이터를 불러오지 못했습니다.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleDelete = async (card: MesDashboardCard) => {
    if (!window.confirm(`'${card.title}' 카드를 삭제할까요?`)) return;
    try {
      await companyKpiUseCase.deleteCard(card.id);
      await loadCards();
    } catch (e) {
      alert(e instanceof Error ? e.message : "삭제 실패");
    }
  };

  // 지표당 1카드 — 6종 모두 등록되면 더 추가 불가.
  const usedTypes = new Set(
    cards.flatMap((c) => c.metrics.map((m) => m.metricType)),
  );
  const canAdd = usedTypes.size < KPI_TYPE_OPTIONS.length;

  return (
    <CompanyAdminLayout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6 text-left">
          <div className="text-left">
            <h1 className="text-[22px] font-bold text-[#222] text-left">KPI</h1>
            <p className="text-[13px] text-[#888] mt-1 text-left">
              우리 회사 KPI를 직접 구성하고 현재값·목표 대비 달성률을 확인하세요.
            </p>
          </div>
          {canAdd && (
            <Button variant="primary" onClick={() => setEditor({ card: null })}>
              + KPI 추가
            </Button>
          )}
        </div>

        {loading ? (
          <div className="text-[14px] text-[#888] py-20 text-center">불러오는 중…</div>
        ) : error ? (
          <div className="text-[14px] text-[#F24A4A] py-20 text-center">{error}</div>
        ) : cards.length === 0 ? (
          <div className="border border-dashed border-[#e0e0e0] rounded-[12px] py-20 text-center text-[#888]">
            <div className="text-[14px] mb-3">아직 등록된 KPI가 없습니다.</div>
            <Button variant="outline" onClick={() => setEditor({ card: null })}>
              첫 KPI 만들기
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
            {cards.map((card) => (
              <KpiCard
                key={card.id}
                card={card}
                onEdit={(c) => setEditor({ card: c })}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}

        {/* 추가/수정은 이전 모달 방식 */}
        {editor && (
          <CardEditorModal
            initial={editor.card}
            machines={machines}
            usedMetricTypes={cards.flatMap((c) => c.metrics.map((m) => m.metricType))}
            onClose={() => setEditor(null)}
            onSaved={async () => {
              setEditor(null);
              await loadCards();
            }}
          />
        )}
      </div>
    </CompanyAdminLayout>
  );
};